/**
 * useTrackPhases — fetches Whisper word-level phrase markers for a given
 * track id from `/api/audio/phases/<trackId>` (the public route exposed
 * by `admin_audio_resync.public_router`).
 *
 * Returns `{ phrases, duration, loading, ready }`.
 *   - phrases:  [{ start, end, text, words: [{word,start,end}] }] or null
 *   - duration: seconds (from Whisper) or null
 *   - loading:  true while the fetch is in flight
 *   - ready:    true once the request has settled (success or 404)
 *
 * 404 responses (track has no override yet) resolve cleanly with
 * `phrases=null` so callers can fall back to their hardcoded phrase
 * list without log noise.
 *
 * Used by every CapCut-synced ritual / alignment / reset player so they
 * pick up new word-level data the moment a track is resynced.
 */
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export const useTrackPhases = (trackId, { enabled = true } = {}) => {
  const [state, setState] = useState({ phrases: null, duration: null, loading: false, ready: false });

  useEffect(() => {
    if (!enabled || !trackId) {
      setState({ phrases: null, duration: null, loading: false, ready: true });
      return undefined;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, ready: false }));
    fetch(`${API}/api/audio/phases/${encodeURIComponent(trackId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d && Array.isArray(d.phrases) && d.phrases.length) {
          setState({ phrases: d.phrases, duration: d.duration || null, loading: false, ready: true });
        } else {
          setState({ phrases: null, duration: null, loading: false, ready: true });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setState({ phrases: null, duration: null, loading: false, ready: true });
      });
    return () => { cancelled = true; };
  }, [trackId, enabled]);

  return state;
};

export default useTrackPhases;
